export const safeNumber = (value: any, defaultValue: number = 0): number => {
  if (value === null || value === undefined) return defaultValue;
  const num = Number(value);
  return isNaN(num) || !isFinite(num) ? defaultValue : num;
};

export function calculatePriceStatistics(opportunities: any[]) {
  if (!opportunities || opportunities.length === 0) {
    return {
      zeroPercentage: 0,
      reliableZeroHours: [],
      reliableWindow: { start: 0, end: 0 },
      avgSpread: 0
    };
  }

  const prices = opportunities.map(o => o.spotPrice);
  const zeroCount = prices.filter(p => p < 1).length;

  const byHour: { [key: number]: number[] } = {};
  opportunities.forEach(o => {
    if (!byHour[o.hour]) byHour[o.hour] = [];
    byHour[o.hour].push(o.spotPrice);
  });

  const reliableZeroHours: number[] = [];
  Object.entries(byHour).forEach(([hour, prices]) => {
    const zeroPercent = prices.length > 0 ? prices.filter(p => p < 1).length / prices.length : 0;
    if (zeroPercent > 0.8) {
      reliableZeroHours.push(parseInt(hour));
    }
  });

  const minPrice = prices.length > 0 ? Math.min(...prices) : 0;
  const maxPrice = prices.length > 0 ? Math.max(...prices) : 0;

  return {
    zeroPercentage: prices.length > 0 ? (zeroCount / prices.length) * 100 : 0,
    reliableZeroHours: reliableZeroHours.sort((a, b) => a - b),
    reliableWindow: {
      start: reliableZeroHours.length > 0 ? Math.min(...reliableZeroHours) : 0,
      end: reliableZeroHours.length > 0 ? Math.max(...reliableZeroHours) : 0
    },
    avgSpread: maxPrice - minPrice
  };
}

export function calculateConsumptionStatistics(consumptionData: any[]) {
  if (!consumptionData || consumptionData.length === 0) {
    return {
      baseload: 0,
      peakLoad: 0,
      average: 0,
      loadFactor: 0,
      shiftablePortion: 0,
      variability: 'unknown'
    };
  }

  const values = consumptionData.map(c => safeNumber(c.consumption, 0));
  const baseload = values.length > 0 ? Math.min(...values) : 0;
  const peakLoad = values.length > 0 ? Math.max(...values) : 0;
  const avgLoad = values.length > 0 ? values.reduce((a, b) => a + b, 0) / values.length : 0;

  const stdDev = values.length > 0 ? Math.sqrt(
    values.reduce((sum, val) => sum + Math.pow(val - avgLoad, 2), 0) / values.length
  ) : 0;

  const loadFactor = peakLoad > 0 ? avgLoad / peakLoad : 0;
  const shiftablePortion = avgLoad > 0 ? (peakLoad - baseload) / avgLoad : 0;
  const cvRatio = avgLoad > 0 ? stdDev / avgLoad : 0;

  return {
    baseload: safeNumber(baseload),
    peakLoad: safeNumber(peakLoad),
    average: safeNumber(avgLoad),
    loadFactor: safeNumber(loadFactor),
    shiftablePortion: safeNumber(shiftablePortion),
    variability: cvRatio < 0.3 ? 'low' : cvRatio < 0.6 ? 'medium' : 'high'
  };
}

export function calculateConfidence(priceStats: any, consumptionStats: any) {
  const pricePredictability =
    (priceStats.reliableZeroHours.length / 24) * 40 +
    (priceStats.zeroPercentage / 100) * 60;

  const consumptionFlexibility =
    consumptionStats.shiftablePortion * 50 +
    consumptionStats.loadFactor * 50;

  const alignmentScore = 50;
  const currentValueCapture = priceStats.zeroPercentage * 0.4;

  const overall = (pricePredictability * 0.4 + consumptionFlexibility * 0.4 + alignmentScore * 0.2);
  const achievablePercentage = overall / 100;

  return {
    pricePredictability: safeNumber(pricePredictability),
    consumptionFlexibility: safeNumber(consumptionFlexibility),
    alignmentScore: safeNumber(alignmentScore),
    currentValueCapture: safeNumber(currentValueCapture),
    overall: safeNumber(overall),
    achievablePercentage: safeNumber(achievablePercentage),
    factors: {
      price: {
        score: safeNumber(pricePredictability),
        weight: 0.4,
        contribution: safeNumber(pricePredictability * 0.4)
      },
      consumption: {
        score: safeNumber(consumptionFlexibility),
        weight: 0.4,
        contribution: safeNumber(consumptionFlexibility * 0.4)
      },
      alignment: {
        score: safeNumber(alignmentScore),
        weight: 0.2,
        contribution: safeNumber(alignmentScore * 0.2)
      }
    }
  };
}